import axios from "axios";
import React, { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export const SectionsContext = createContext({
  footer: {
    newsletter: {
      heading: "Stay Updated with Our Newsletter",
      description:
        "Sign up for our newsletter to receive the latest updates, health tips, and special offers from Brooks Health and Care Limited delivered directly to your inbox.",
      uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726642042/brooks/newhomesegment8bgIMG.98bb3626fd3417a8e445.jpg",
      cta: "Subscribe",
    },
    commitment:
      "We are your trusted companion in delivering empathetic, extensive and tailored home health care services.",
    social: {
      heading: "Follow Us",
      platforms: [
        {
          name: "facebook",
          uri: "https://www.facebook.com/profile.php?id=61557096390057",
        },
        {
          name: "instagram",
          uri: "https://www.instagram.com/brookshealthandcareltd",
        },
        {
          name: "whatsapp",
          uri: "https://api.whatsapp.com/send?phone=447449367921",
        },
      ],
    },
    copyright: {
      text: "Copyright © 2024 Brooks Health and Care Limited. All rights reserved.",
    },
    services: [
      "Home Care Support",
      "Live in Care",
      "Wake in Nights",
      "Companionship",
      "Community Aid",
    ],
  },
  stats: {
    clientSatisfied: "95%",
    careHoursDelivered: "200k+",
    yearsExperience: "3+",
    cta: "Ready to experience the warmth of compassionate care and personalized support?",
    uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726640514/brooks/homeSegment2IMG.7e36d48bc5045e360f3f.jpg",
  },
  reviews: {
    heading: "Reviews",
    intro:
      "Our commitment to excellence is reflected in the words of our satisfied service users",
    items: [
      {
        title: "Compassionate",
        body: "My husband is currently being cared for by a live-in carer, giving me a week of respite. The care agency listened to our needs and have provided a carer who has the right experience, and attitude. Her approach and understanding shows exceptional care and compassion, and I have full confidence in her, knowing that she will respect his wishes and look after his needs.",
        author: "C S",
        stars: 5,
      },
      {
        title: "Attentive Care",
        body: "We have had home care for two weeks for my mother following her time in hospital. It is early days to comment but overall we have been pleased with the care that they have provided. Difficult to give a more detailed report as we have had a variety of carers up until now.",
        author: "Gillian B",
        stars: 4,
      },
      {
        title: "Reliable Support",
        body: "Good and punctual service. The carer is friendly, polite and helpful, would recommend your service to other clients.",
        author: "B B",
        stars: 5,
      },
    ],
  },
  faq: {
    heading: "Frequently Asked Questions",
    items: [
      {
        question: "Will I have to pay for my care?",
        answer:
          "You may have to pay for your care; however, you might be eligible for assistance with funding - this will depend on your situation. You will need to contact your local council, who will carry out a care assessment to determine the level of care you require. They will then carry out a financial assessment to determine your ability to pay for or contribute towards the cost of this care. If your loved one is assessed as needing nursing care, regardless of their situation, they may be entitled to either the Funded Nursing Care (FNC) contribution or full funding, depending on their needs.",
      },
      {
        question:
          "How can I provide feedback or address concerns about the care I or my loved one received?",
        answer:
          "We welcome feedback from our clients and their families. You can contact our customer service team directly to provide feedback, address concerns, or make suggestions for improvement. Your satisfaction and well-being are important to us, and we are committed to addressing any issues promptly.",
      },
      {
        question: "How do I arrange for healthcare services?",
        answer:
          "You can arrange for our services by contacting us directly via phone or email. We will schedule a consultation to assess your needs and develop a personalised care plan that meets your requirements.",
      },
      {
        question: "What are your prices like?",
        answer:
          "A member of our team will be able to give you a ‘starting from’ price if your relative or friend has got to fund the care themselves, so you are able to get an idea of how much the weekly cost will begin from. We then complete a consultation assessment to determine someone’s care and support needs before deciding on how many calls they would need. At this point, we will be able to confirm whether we can meet their needs and the hourly fee.",
      },
      {
        question: "Is there any other financial help available?",
        answer:
          "Attendance Allowance is a non-means-tested, tax-free state benefit payable to all people over 65 who have a long-term illness or disability. The weekly amount varies depending on whether someone needs help during the day, at night, or both days and nights. It can continue to be paid, provided the Service User pays for the care themselves and is not funded by the local authority.",
      },
      {
        question: "What happens if the money runs out?",
        answer:
          "If someone is funding their own care and their savings fall below the amount set by the government, the local authority may fund all or part of the care fees, provided they meet eligibility criteria. However, the local authority has a maximum amount they are willing to pay, and any shortfall may need to be covered by a third-party contribution.",
      },
      {
        question:
          "Can I pick my carer based on their gender, and can I retain the same carer for my care package?",
        answer:
          "Yes, you can select the gender of your carer. You would also be allocated three different carers to enable the rotation of care, so you can get used to them.",
      },
    ],
  },
  home: {
    meta: {
      title: "Home - Brooks Health and Care",
      description:
        "Brooks Health and Care offers compassionate and personalized health care services tailored to your unique needs. Experience the best care at home with us.",
      keywords:
        "home care, health care, personalized care, medical staffing, palliative care",
    },
    hero: {
      headline: "HEALTH AND CARE COMPANIONSHIP",
      subheadline: "Embracing Your Journey to Wellness",
      description:
        "Welcome to Brooks Health and Care, where we understand that your well-being is unique and deserves personalized attention.",
      cta: "Book Appointment",
      uris: [
        "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726640132/brooks/heroboximg1.737a65b0b479bb6026d0.jpg",
        "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726640010/brooks/newheroboximg2.12f33b335063f76b05dc.jpg",
        "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726640076/brooks/newheroboximg3.963de7ecb52fbaa2a150.jpg",
      ],
    },
    about: {
      heading: "About Us",
      summary:
        "<p>We are a reliable company that provides compassionate and personalised healthcare services. Our range of services is tailored to meet the unique needs of each individual we serve, including Home Care Support, Personalised Caregiving, and Medical Staffing.</p><p><br></p><p> </p><p><br></p><p>Our ultimate goal is to provide exceptional care to those who depend on us, ensuring they receive unparalleled service and support. At Brooks Health and Care, we understand that your health and wellbeing are of utmost importance. That's why we are dedicated to providing you with unwavering support, listening to your concerns, and working collaboratively with you to meet your unique needs. We're here to help you make informed decisions and put you back in control of your health journey.We know that empowerment is the key to having control over the decisions and actions that shape our lives.</p><p><br></p><p> </p><p><br></p><p><br></p><p> </p><p>WE ARE HERE FOR YOU.</p>",
    },
    whatWeDo: {
      heading: "WHAT WE DO",
      intro:
        "At Brooks Health and Care, we prioritize your well-being above all else",
      items: [
        {
          title: "Care Services",
          description:
            "We provide compassionate and reliable care services services, enabling our clients to live independently and comfortably in their homes.",
          uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726607445/brooks/homeSegment3img1s.d53d6ad477319d454957.jpg",
        },
        {
          title: "Personalized Caregiving",
          description:
            "Our personalised caregiving services are designed to cater to each Service User’s unique needs and preferences, ensuring that they receive the highest quality of care possible.",
          uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726638580/brooks/homeSegment3img2.8d7f9499644264c38194.jpg",
        },
        {
          title: "Medical Staffing",
          description:
            "Our exceptional medical staffing team guarantees that you receive the best care and support possible. With years of experience and expert knowledge, we ensure that you and your loved ones are in safe hands, giving you the peace of mind you deserve.",
          uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726639373/brooks/homeSegment3img3.aca856f7b783c2bf0beb.jpg",
        },
        {
          title: "Palliative Care Services",
          description:
            "Our palliative care services are designed to provide the utmost comfort and support to both patients and their loved ones. We understand that difficult times call for compassionate care, and we are here to help you navigate through them with ease.",
          uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726639447/brooks/homeSegment3img4.16224054bd4afb5d21de.jpg",
        },
      ],
    },
    whyChooseUs: {
      heading: "WHY CHOOSE BROOKS?",
      intro:
        "Discover why Brooks Health and Care is your top choice for health care services.",
      uris: [
        "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726639566/brooks/secondimg.1bd7627cc646c6714ebc.jpg",
        "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726639618/brooks/homeSegment5IMG.4ad7ebb21bdd2c820544.jpg",
      ],
      items: [
        {
          title: "Comprehensive Services",
          description:
            "Brooks Health and Care offers a comprehensive range of services, including care services, personalised caregiving, medical staffing, and palliative care. Whatever your needs, we have the services to support you.",
        },
        {
          title: "Personalized Approach",
          description:
            "We understand that each individual's needs are unique. That's why we take a personalized approach to care, tailoring our services to meet the specific needs and preferences of each client.",
        },
        {
          title: "Experienced Team",
          description:
            "With years of experience in the healthcare industry, our team brings a wealth of knowledge and expertise to every interaction. From support workers, carers to nurses, we have the skills and experience.",
        },
        {
          title: "Compassionate Care",
          description:
            "At Brooks Health and Care, we prioritize compassion in every interaction. Our dedicated team of caregivers and healthcare professionals is committed to providing empathetic and supportive care to our clients.",
        },
        {
          title: "Commitment to Excellence",
          description:
            "We are dedicated to upholding the highest standards of excellence in everything we do. From our rigorous caregiver training programs to our commitment to continuous improvement, we strive for excellence in all aspects.",
        },
        {
          title: "Trusted Partner",
          description:
            "When you choose Brooks Health and Care, We are committed to building long-lasting relationships based on trust, reliability, and exceptional care. For our partners that we supply staff to and our home care service users, you're choosing a trusted partner and experienced professionals ready to deliver quality and exceptional care to the community. We pride ourselves on not disappointing our customers or service users so far.",
        },
      ],
    },
    partners: {
      heading: "OUR PARTNERS",
      intro: "Discover Brooks Health and Care's Partners",
      items: [
        {
          text: "We are regulated by CQC",
          uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726639796/brooks/partner2.987d7725211dfde70b08.png",
        },
        {
          text: "We are registered with the ICO.",
          uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726639844/brooks/partner1.51481e8044dfe69a90dd.jpg",
        },
        {
          text: "We are registered with Homecare.co.uk",
          uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726639900/brooks/partner3.f4e6a51146f2eeaf2bc6.jpg",
        },
      ],
    },
    faq: {
      heading: "Frequently Asked Questions",
      intro: "Get answers about Brooks Health and Care's Services.",
      items: [
        {
          question: "Will I have to pay for my care?",
          answer:
            "You may have to pay for your care; however, you might be eligible for assistance with funding - this will depend on your situation. You will need to contact your local council, who will carry out a care assessment to determine the level of care you require. They will then carry out a financial assessment to determine your ability to pay for or contribute towards the cost of this care. If your loved one is assessed as needing nursing care, regardless of their situation, they may be entitled to either the Funded Nursing Care (FNC) contribution or full funding, depending on their needs.",
        },
        {
          question:
            "How can I provide feedback or address concerns about the care I or my loved one received?",
          answer:
            "We welcome feedback from our clients and their families. You can contact our customer service team directly to provide feedback, address concerns, or make suggestions for improvement. Your satisfaction and well-being are important to us, and we are committed to addressing any issues promptly.",
        },
        {
          question: "How do I arrange for healthcare services?",
          answer:
            "You can arrange for our services by contacting us directly via phone or email. We will schedule a consultation to assess your needs and develop a personalised care plan that meets your requirements.",
        },
        {
          question: "What are your prices like?",
          answer:
            "A member of our team will be able to give you a ‘starting from’ price if your relative or friend has got to fund the care themselves, so you are able to get an idea of how much the weekly cost will begin from. We then complete a consultation assessment to determine someone’s care and support needs before deciding on how many calls they would need. At this point, we will be able to confirm whether we can meet their needs and the hourly fee.",
        },
        {
          question: "Is there any other financial help available?",
          answer:
            "Attendance Allowance is a non-means-tested, tax-free state benefit payable to all people over 65 who have a long-term illness or disability. The weekly amount varies depending on whether someone needs help during the day, at night, or both days and nights. It can continue to be paid, provided the Service User pays for the care themselves and is not funded by the local authority.",
        },
        {
          question: "What happens if the money runs out?",
          answer:
            "If someone is funding their own care and their savings fall below the amount set by the government, the local authority may fund all or part of the care fees, provided they meet eligibility criteria. However, the local authority has a maximum amount they are willing to pay, and any shortfall may need to be covered by a third-party contribution.",
        },
        {
          question:
            "Can I pick my carer based on their gender, and can I retain the same carer for my care package?",
          answer:
            "Yes, you can select the gender of your carer. You would also be allocated three different carers to enable the rotation of care, so you can get used to them.",
        },
      ],
    },
  },
  about: {
    meta: {
      title: "About Us - Brooks Health and Care",
      description:
        "Learn more about Brooks Health and Care, our mission, our compassionate approach, and the team dedicated to providing exceptional care.",
      keywords:
        "about brooks care, health care, mission, compassionate care, personalized care",
    },
    hero: {
      headline: "ABOUT BROOKS HEALTH AND CARE",
      subheadline: "Caring for You Every Step of the Way",
      uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726648004/brooks/newabouthero.8aa9e576227187f10bf9.jpg",
    },
    summary:
      "We are a reliable company that provides compassionate and personalised healthcare services. Our range of services is tailored to meet the unique needs of each individual we serve, including Home Care Support, Personalised Caregiving, and Medical Staffing.<br> <br>Our ultimate goal is to provide exceptional care to those who depend on us, ensuring they receive unparalleled service and support. At Brooks Health and Care, we understand that your health and wellbeing are of utmost importance. That's why we are dedicated to providing you with unwavering support, listening to your concerns, and working collaboratively with you to meet your unique needs. We're here to help you make informed decisions and put you back in control of your health journey.We know that empowerment is the key to having control over the decisions and actions that shape our lives.<br> <br><br> <span class=\"fw-bold\">WE ARE HERE FOR YOU.</span>",
    mission: {
      heading: "OUR MISSION",
      intro: "At Brooks Health and Care, our mission is to:",
      list: [
        "Support our service users in maintaining their independence and quality of life in their homes and communities.",
        "Provide top-tier service with a friendly, caring, and empathetic approach.",
        "Provide appropriate, trained, and skilled staff that fit our service users' needs.",
        "Provide continuity of care wherever possible.",
        "Respect the dignity and privacy of our service users while promoting their utmost wellbeing and development.",
      ],
    },
    vision: {
      heading: "OUR VISION",
      intro: "At Brooks Health and Care, our vision is to:",
      body: "At Brooks Health and Care, we're all about providing first-class care for our service users. Our mission is to maintain their independence and quality of life in their homes and communities while respecting their dignity and promoting well-being and development. We achieve this by recruiting, training, and retaining the best staff who share our compassion, respect, and honesty values. We believe in working collaboratively with everyone involved in caring for our service users, and we always aim to provide the best possible service with a friendly, caring, and empathetic approach. So, if you're looking for care that genuinely cares, choose Brooks Health and Care - where first-class care is our top priority!",
    },
    value: {
      heading: "Our Values",
      description:
        "\nTo achieve our vision and mission, we have developed a set of values that run through our care, recruitment, training, and development processes. Our values include compassion and empathy, respect and dignity, openness and honesty, collaborative working, commitment to quality of care, and caring with a smile.",
      items: [
        {
          subheading: "Compassion and Empathy",
          description:
            "They are at the heart of everything we do for our service users. They combine humanity, kindness, and care.",
        },
        {
          subheading: "Respect and Dignity",
          description:
            "We value each of our service users as individuals and use a person-centred approach. We respect their aspirations and commitments and seek to understand and promote their priorities, needs, abilities, and limitations. When providing care, we treat our service users with dignity, aim to support and empower them and respect their right to make their own decisions.",
        },
        {
          subheading: "Openness and Honesty",
          description:
            "We value each of our service users as individuals and use a person-centred approach. We respect their aspirations and commitments and seek to understand and promote their priorities, needs, abilities, and limitations. When providing care, we treat our service users with dignity, aim to support and empower them and respect their right to make their own decisions.",
        },
        {
          subheading: "Collaborative Working",
          description:
            "This is key to our success. We consider the views of all those involved in caring for our service users, including social workers, family members, friends, doctors, nurses, office staff, carers, and support workers. Each carer and support worker are a valued part of our team, and we listen to them and value their contribution and input.",
        },
        {
          subheading: "Commitment to Quality of Care",
          description:
            "We can only provide the best possible care and support if we recruit, train, and retain the best staff who share our values and vision. We value our staff's commitment and passion for providing our clients with great care, and we recognise that mistakes do occur. When they do, we expect an honest reflection from all those involved to prevent future errors.",
        },
        {
          subheading: "Caring with a Smile",
          description:
            "Everyone responds with a smile. No matter how busy life is, there is always time for a smile. We aim to recruit cheerful and empathetic staff and expect them to try to leave their problems at home and to do their best to bring a smile to all those they see or speak to during the working day. This applies to office staff using phones for much of the day.<br> <br>At Brooks Health and Care, we're all about providing care that genuinely cares. Choose us for first-class care that puts our service users' needs and well-being first.",
        },
      ],
    },
    whyChooseUs: {
      heading: "WHY CHOOSE US?",
      intro:
        "Discover why Brooks Health and Care is your top choice for home health care.",
      items: [
        {
          title: "Compassionate Care",
          description:
            "At Brooks Health and Care, we prioritize compassion in every interaction. Our dedicated team of caregivers and healthcare professionals is committed to providing empathetic and supportive care to our service users.",
        },
        {
          title: "Personalized Approach",
          description:
            "We understand that each individual's needs are unique. That's why we take a personalized approach to care, tailoring our services to meet the specific needs and preferences of each client.",
        },
        {
          title: "Experienced Team",
          description:
            "With years of experience in the healthcare industry, our team brings a wealth of knowledge and expertise to every interaction. From support workers, carers to nurses, we have the skills and experience.",
        },
        {
          title: "Comprehensive Services",
          description:
            "We offer a range of healthcare services, including Domiciliary Care (Home Care Support), Palliative Care, Companionship, Live In Care, Nights/Sleep-In, Wake-In Nights, Respite, 1-1 Support, Care Support for Travel and more, Medical Staffing Whatever your needs, we have the services to support you.",
        },
        {
          title: "Commitment to Excellence",
          description:
            "We are dedicated to upholding the highest standards of excellence in everything we do. From our rigorous caregiver training programs to our commitment to continuous improvement, we strive for excellence in all aspects.",
        },
        {
          title: "Trusted Partner",
          description:
            "When you choose Brooks Health and Care, you're choosing a trusted partner in your health journey. We are committed to building long-lasting relationships based on trust, reliability, and exceptional care.",
        },
      ],
    },
  },
  services: {
    meta: {
      title: "Our Services - Brooks Health and Care",
      description:
        "Explore the wide range of personalized health and care services offered by Brooks Health and Care, designed to support your well-being.",
      keywords:
        "health services, personalized care, domiciliary care, elderly care, home care, health and care solutions",
    },
    hero: {
      headline: " BROOKS HEALTH AND CARE SERVICES",
      subheadline: "Our Comprehensive Care Services",
      uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726648198/brooks/serviceshero.9056d6e50ca236e9a635.jpg",
    },
    summary:
      "Welcome to Brooks Health and Care's Services Page. Explore our comprehensive range of care services designed to meet your unique needs and preferences. From personalized caregiving to medical staffing solutions, our dedicated team is here to provide compassionate care and support for you and your loved ones.",
    careServices: {
      title: "Care Services",
      intro: "Discover the comfort of home with our care services.",
      items: [
        {
          title: "Direct Home Care Support",
          description:
            "Providing assistance with activities of daily living in the comfort of your own home.",
        },
        {
          title: "Personal Care",
          description:
            "Tailored personal care services to meet your specific needs.",
        },
        {
          title: "Live-in Care",
          description:
            "24/7 care and support from a dedicated caregiver who resides in your home.",
        },
        {
          title: "Companionship",
          description:
            "Social interaction and companionship to combat loneliness and isolation.",
        },
        {
          title: "Respite",
          description:
            "Temporary relief for primary caregivers, allowing them to take a break while their loved one receives care.",
        },
        {
          title: "Nights/Sleep-in",
          description:
            "Overnight care services for peace of mind and support during the night.",
        },
        {
          title: "Wake-in Nights",
          description:
            "Assistance and support during the night for those who require wakeful supervision.",
        },
        {
          title: "1-1 Support",
          description:
            "Individualized support tailored to your unique needs and preferences.",
        },
        {
          title: "Care Support for Travel, and more",
          description:
            "Accompanying individuals on trips and providing support during travel.",
        },
      ],
    },
    agencyService: {
      title: "AGENCY SUPPLY SERVICE",
      intro: "Optimize staffing with our Agency Supply Service.",
      items: [
        {
          title: "Covering Staff",
          description:
            "Providing coverage for healthcare facilities to ensure optimal staffing levels at all times.",
        },
        {
          title: "Supply of Temporary and Permanent Staff",
          description:
            "Supplying qualified healthcare professionals for temporary or permanent positions as needed.",
        },
        {
          title: "Temp to Permanent Staff",
          description:
            "Offering temporary staffing solutions with the option to transition to permanent employment.",
        },
      ],
    },
    staff: {
      title: "TYPES OF STAFF",
      intro: "Meet our diverse team of dedicated professionals.",
      items: [
        {
          title: "Healthcare Assistant",
          description:
            "Assisting with activities of daily living and providing basic medical care under the supervision of a registered nurse.",
        },
        {
          title: "Nurses",
          description:
            "Providing specialized care and support for individuals with health conditions.",
        },
        {
          title: "Support Worker",
          description:
            "Offering emotional support, companionship, and assistance with daily tasks for individuals in need.",
        },
      ],
    },
  },
  careers: {
    meta: {
      title: "Careers - Brooks Health and Care",
      description:
        "Join the team at Brooks Health and Care. Explore exciting career opportunities in health care and make a difference in the lives of others.",
      keywords: "",
    },
    hero: {
      headline: "JOIN OUR TEAM AT BROOKS HEALTH AND CARE",
      subheadline: "Explore Exciting Career Opportunities with Brooks",
      uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726648289/brooks/jobhero.3f2c90119fbd7c09ebe4.jpg",
    },
    summary:
      "Welcome to our Careers page! Discover fulfilling opportunities to join our dynamic team and contribute to our mission of Brooks Health and Care. Explore our current job openings below and take the first step towards an exciting career with us.",
    jobOpenings: {
      heading: "Current Job Openings",
      items: [
        {
          title: "Domiciliary Care Worker with Full UK Driving Licence",
          location: "Biggleswade, Bedford",
          summary:
            "We are seeking a dedicated and compassionate Domiciliary Care Worker with a full and valid UK driving licence to join our team. As a Domiciliary Care Worker, you will provide personal care and support to clients in their homes, promoting their independence and enhancing their quality of life. This role requires travel between clients’ homes, so a valid UK driving licence and access to a reliable vehicle are essential.",
          responsibilities: [
            "Provide personal care and assistance with daily living activities, including bathing, dressing, and grooming.",
            "Assist clients with mobility and physical activities, ensuring their safety and comfort.",
            "Administer medication according to care plans and company policies.",
            "Prepare and serve meals, ensuring dietary requirements and preferences are met.",
            "Perform light household tasks such as cleaning, laundry, and shopping.",
            "Provide companionship and emotional support to clients.",
            "Monitor and report any changes in clients’ health or wellbeing to the appropriate professionals.",
            "Maintain accurate and up-to-date records of care provided.",
            "Follow care plans and instructions from healthcare professionals and family members.",
            "Comply with all health and safety regulations and company policies.",
          ],
          requirements: [
            "Valid Full UK driving licence and access to a reliable vehicle.",
            "Previous experience in a similar care role is preferred but not essential; full training will be provided.",
            "NVQ Level 2 in Health and Social Care or willingness to work towards this qualification.",
            "Strong communication and interpersonal skills.",
            "Compassionate, patient, and empathetic nature.",
            "Ability to work independently and as part of a team.",
            "Flexibility to work various shifts, including mornings, evenings, weekends, and bank holidays.",
            "Right to work in the UK.",
            "Preferable live in a commutable distance in the area.",
          ],
          benefits: [
            "Competitive salary with opportunities for overtime.",
            "Flexible working hours to suit your lifestyle.",
            "Comprehensive training and ongoing professional development.",
            "Supportive and friendly working environment.",
            "Mileage allowance for travel between clients’ homes.",
            "Pension scheme.",
            "Employee Assistance Programme.",
            "Opportunities for career progression within the company.",
          ],
          equalOpportunities:
            "Brooks Health and Care is an equal opportunities employer. We welcome applications from all suitably qualified persons regardless of their race, sex, disability, religion/belief, sexual orientation, or age.",
          disability:
            'The Equality Act 2010 defines disability as “a physical or mental impairment which has a substantial and long term adverse affect on a person‘s ability to carry out normal day to day activities". (You do not need to be registered as disabled). We are a ‘positive about disability’ employer. We welcome applicants with disabilities and we will guarantee an interview to anyone with a disability who meets the minimum essential criteria for the post. The recruitment team will be in contact prior to the interview to see if you need any assistance or reasonable adjustments to be made with interview arrangements',
        },
      ],
    },
    vacancyImage:
      "https://res.cloudinary.com/dtgigdp2j/image/upload/v1728008651/brooks/vacancy.2aacf2118cbaa1a3c24d.jpg",
    whyJoinUs: {
      title: "WHY JOIN US AT BROOKS HEALTH AND CARE?",
      intro:
        "At Brooks Health and Care, we're a dedicate community making a difference. Here's why you hould join us:",
      items: [
        {
          title: "Making a Positive Impact",
          description:
            "Every day, our team members at Brooks Health and Care have the opportunity to make a positive impact on the lives of others. Whether it's providing compassionate care to our service users or developing innovative solutions, your work will have purpose and significance.",
        },
        {
          title: "Collaborative Environment",
          description:
            "We believe in the power of collaboration and teamwork at Brooks Health and Care. You'll have the chance to work alongside talented individuals who are passionate about what they do, share ideas, and support each other's growth and success.",
        },
        {
          title: "Professional Development",
          description:
            "At Brooks Health and Care, we invest in our employees' growth and development. From ongoing training opportunities to mentorship programs, we're committed to helping you reach your full potential and achieve your career goals.",
        },
        {
          title: "Inclusive Culture",
          description:
            "Diversity and inclusion are at the heart of everything we do at Brooks Health and Care. We celebrate differences, foster a culture of respect and belonging, and strive to create an environment where everyone feels valued and empowered to succeed.",
        },
        {
          title: "Work-Life Balance",
          description:
            "We understand the importance of maintaining a healthy work-life balance at Brooks Health and Care. That's why we offer flexible scheduling options, generous time off, and supportive policies that prioritize your well-being and allow you to thrive both personally and professionally.",
        },
        {
          title: "Competitive Benefits",
          description:
            "In addition to a rewarding work experience, we offer competitive compensation packages and a comprehensive benefits program at Brooks Health and Care. From healthcare coverage to retirement savings plans, we provide the support and resources you need to thrive.",
        },
      ],
    },
    closing:
      "Join us at Brooks Health and Care and become part of a team that's making a difference in the lives of others every day. Together, we can create a brighter future for our community and beyond.",
  },
  contact: {
    meta: {
      title: "Contact Us - Brooks Health and Care",
      description:
        "Contact Brooks Health and Care today. We’re here to answer any questions and provide the support you need.",
      keywords:
        "contact, get in touch, contact brooks health, health care support",
    },
    hero: {
      headline: "JOIN OUR TEAM AT BROOKS HEALTH AND CARE",
      subheadline: "Explore Exciting Career Opportunities with Brooks",
      uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1726648471/brooks/contacthero.24ba9cdba0cd0951c7b6.jpg",
    },
    summary:
      "Have questions or need assistance? We're here to help. Feel free to reach out to us via phone, email, or by filling out the contact form below.",
    connect: {
      title: "Connect with Us Today!",
      intro:
        "At Brooks Health and Care, we value your inquiries, feedback, and collaborations. Whether you are interested in our services, or have questions about our projects.",
    },
    faq: {
      heading: "Frequently Asked Questions",
      intro: "Get answers about Brooks Health and Care's care services.",
      items: [
        {
          question: "What types of services does Brooks Health and Care offer?",
          answer:
            "We offer a range of home health care services, including Care Services, Personalized Caregiving, Medical Staffing, and Palliative Care Services. Our services are tailored to meet the unique needs and preferences of each individual we serve.",
        },
        {
          question:
            "How do I know if home health care is right for me or my loved one?",
          answer:
            "Home health care may be suitable for individuals who require assistance with activities of daily living, medical support, or companionship in the comfort of their own homes. We offer free consultations to assess your needs and discuss the best care options for you or your loved one.",
        },
        {
          question: "Are your caregivers trained and certified?",
          answer:
            "Yes, all our caregivers are highly trained, experienced, and certified professionals. They undergo thorough background checks and regular training to ensure they provide the highest quality of care to our service users.",
        },
        {
          question: "How do I arrange for home health care services?",
          answer:
            "You can arrange for our services by contacting us directly via phone or email. We will schedule a consultation to assess your needs and develop a personalized care plan that meets your requirements.",
        },
        {
          question: "Is home health care covered by insurance?",
          answer:
            "Home health care coverage varies depending on your insurance provider and your specific policy. We recommend contacting your insurance company to verify coverage details. We also offer flexible payment options to accommodate your financial situation.",
        },
        {
          question: "Can I request a specific caregiver?",
          answer:
            "Yes, we understand the importance of building trust and rapport with your caregiver. While we cannot guarantee the availability of a specific caregiver at all times, we will do our best to accommodate your preferences whenever possible.",
        },
        {
          question:
            "How can I provide feedback or address concerns about the care I or my loved one received?",
          answer:
            "We welcome feedback from our service users and their families. You can contact our customer service team directly to provide feedback, address concerns, or make suggestions for improvement. Your satisfaction and well-being are important to us, and we are committed to addressing any issues promptly.",
        },
      ],
    },
  },
  blog: {
    meta: {
      title: "Brooks Health and Care Blog",
      description:
        "Stay informed with Brooks Health and Care's latest articles.",
      keywords:
        "elderly care blog, senior wellness, palliative care tips, home care for seniors, aging health advice, Brooks Health and Care, elderly support, senior living",
    },
    hero: {
      headline: "OUR BLOG",
      subheadline: "Welcome To Our Blog",
      uri: "https://res.cloudinary.com/dtgigdp2j/image/upload/v1728034887/brooks/file_oi33qk.jpg",
    },
  },
});

export const SectionsProvider = ({ children }) => {
  const navigate = useNavigate();
  const [sections, setSections] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get("/sections.json");
        setSections(response.data);
      } catch (error) {
        console.error("Error fetching sections", error);
        navigate("/notfound");
      }
    };
    load();
  }, [children, navigate]);

  return (
    <SectionsContext.Provider value={sections}>
      {sections && children}
    </SectionsContext.Provider>
  );
};
